import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Alert, Badge, Box, Button, Group, Modal, NumberInput, Paper, Select, SimpleGrid, Stack, Table, Text, TextInput, Textarea, Title, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconFilterOff, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import api from '../../api/axiosConfig';

const statusLabels = { PENDING: 'Onay Bekliyor', APPROVED: 'Onaylandı', REJECTED: 'Reddedildi', CANCELLED: 'İptal Edildi' };

export default function LeavesPage() {
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [opened, { open, close }] = useDisclosure(false);
  const [rejectingLeave, setRejectingLeave] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [form, setForm] = useState({ employeeId: null, typeId: null, start: '', end: '', reason: '' });

  const loadData = async () => {
    try {
      setLoading(true); setError('');
      const [leaveResponse, employeeResponse, typeResponse] = await Promise.all([api.get('/hr/leaves'), api.get('/hr/employees', { params: { is_active: true } }), api.get('/hr/leave-types')]);
      setLeaves(leaveResponse.data); setEmployees(employeeResponse.data); setLeaveTypes(typeResponse.data);
    } catch (err) { console.error(err); setError(err.response?.data?.detail || 'İzin verileri alınamadı.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadData(); }, []);

  const calculatedDays = form.start && form.end && !dayjs(form.end).isBefore(dayjs(form.start), 'day') ? dayjs(form.end).diff(dayjs(form.start), 'day') + 1 : 0;
  const returnDate = calculatedDays ? dayjs(form.end).add(1, 'day').format('YYYY-MM-DD') : '';
  const filteredLeaves = useMemo(() => leaves.filter((leave) => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return (!q || leave.employee_name.toLocaleLowerCase('tr-TR').includes(q)) && (!locationFilter || leave.location === locationFilter) && (!typeFilter || String(leave.leave_type_id) === typeFilter) && (!statusFilter || leave.status === statusFilter);
  }), [leaves, search, locationFilter, typeFilter, statusFilter]);
  const today = dayjs();
  const todayOnLeave = leaves.filter((item) => item.status === 'APPROVED' && !dayjs(item.start_date).isAfter(today, 'day') && !dayjs(item.end_date).isBefore(today, 'day')).length;
  const waiting = leaves.filter((item) => item.status === 'PENDING').length;
  const thisMonth = leaves.filter((item) => dayjs(item.start_date).format('YYYY-MM') === today.format('YYYY-MM')).length;
  const statusColor = (status) => ({ APPROVED: 'green', PENDING: 'yellow', REJECTED: 'red', CANCELLED: 'gray' }[status] || 'gray');

  const createLeave = async () => {
    try {
      await api.post('/hr/leaves', { employee_id: Number(form.employeeId), leave_type_id: Number(form.typeId), start_date: form.start, end_date: form.end, reason: form.reason.trim() || null });
      setForm({ employeeId: null, typeId: null, start: '', end: '', reason: '' }); close(); await loadData();
    } catch (err) { console.error(err); setError(err.response?.data?.detail || 'İzin talebi oluşturulamadı.'); }
  };
  const approve = async (id) => { try { await api.post(`/hr/leaves/${id}/approve`); await loadData(); } catch (err) { setError(err.response?.data?.detail || 'İzin onaylanamadı.'); } };
  const reject = async () => { try { await api.post(`/hr/leaves/${rejectingLeave.id}/reject`, { rejection_reason: rejectionReason.trim() }); setRejectingLeave(null); setRejectionReason(''); await loadData(); } catch (err) { setError(err.response?.data?.detail || 'İzin reddedilemedi.'); } };
  const resetFilters = () => { setSearch(''); setLocationFilter(null); setTypeFilter(null); setStatusFilter(null); };

return (
  <Box p={{ base: 'md', lg: 'xl' }}>
    <Stack gap="xl">
      {error && (
        <Alert
          color="red"
          title="İzin işlemi başarısız"
          withCloseButton
          onClose={() => setError('')}
        >
          {String(error)}
        </Alert>
      )}

      <Group justify="space-between">
        <div>
          <Title order={2} fw={800}>
            İzin Takibi
          </Title>
          <Text c="dimmed">
            Personel izin planlamaları, talepleri ve onay süreçleri
          </Text>
        </div>

        <Button
          leftSection={<IconPlus size={18} />}
          onClick={open}
        >
          İzin Talebi Oluştur
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        {[
          ['Bugün İzinli', todayOnLeave, 'blue'],
          ['Onay Bekleyen', waiting, 'orange'],
          ['Bu Ayki İzinler', thisMonth, 'grape'],
        ].map(([label, value, color]) => (
          <Paper key={label} withBorder p="lg" radius="xl">
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              {label}
            </Text>
            <Title order={2} c={color}>
              {value}
            </Title>
          </Paper>
        ))}
      </SimpleGrid>

      <Paper withBorder p="md" radius="xl">
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
          <TextInput
            placeholder="Personel ara..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />

          <Select
            placeholder="Tüm lokasyonlar"
            data={[
              ...new Set(
                leaves
                  .map((x) => x.location)
                  .filter(Boolean)
              ),
            ]}
            value={locationFilter}
            onChange={setLocationFilter}
            clearable
          />

          <Select
            placeholder="Tüm izin türleri"
            data={leaveTypes.map((x) => ({
              value: String(x.id),
              label: x.name,
            }))}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
          />

          <Select
            placeholder="Tüm durumlar"
            data={Object.entries(statusLabels).map(([value, label]) => ({
              value,
              label,
            }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
          />

          <Button
            variant="light"
            color="gray"
            leftSection={<IconFilterOff size={16} />}
            onClick={resetFilters}
          >
            Temizle
          </Button>
        </SimpleGrid>
      </Paper>

      <Paper
        withBorder
        radius="xl"
        style={{ overflow: 'hidden' }}
      >
        <Table.ScrollContainer minWidth={920}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Personel</Table.Th>
                <Table.Th>İzin Tipi</Table.Th>
                <Table.Th>Tarih Aralığı</Table.Th>
                <Table.Th>Gün</Table.Th>
                <Table.Th>Talep Tarihi</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th ta="right">İşlemler</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {filteredLeaves.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>
                    <Text fw={700} size="sm">
                      {item.employee_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {item.location}
                    </Text>
                  </Table.Td>

                  <Table.Td>
                    {item.leave_type_name}
                  </Table.Td>

                  <Table.Td>
                    {dayjs(item.start_date).format('DD.MM.YYYY')}
                    {' – '}
                    {dayjs(item.end_date).format('DD.MM.YYYY')}
                  </Table.Td>

                  <Table.Td>
                    {item.total_days}
                  </Table.Td>

                  <Table.Td>
                    {dayjs(item.created_at).format('DD.MM.YYYY')}
                  </Table.Td>

                  <Table.Td>
                    <Badge
                      color={statusColor(item.status)}
                      variant="light"
                    >
                      {statusLabels[item.status] || item.status}
                    </Badge>
                  </Table.Td>

                  <Table.Td ta="right">
                    {item.status === 'PENDING' ? (
                      <Group gap="xs" justify="flex-end">
                        <Tooltip label="Onayla">
                          <ActionIcon
                            color="green"
                            variant="light"
                            onClick={() => approve(item.id)}
                          >
                            <IconCheck size={18} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="Reddet">
                          <ActionIcon
                            color="red"
                            variant="light"
                            onClick={() => {
                              setRejectingLeave(item);
                              setRejectionReason('');
                            }}
                          >
                            <IconX size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    ) : (
                      <Text size="xs" c="dimmed">
                        İşlem tamamlandı
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}

              {!loading && !filteredLeaves.length && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" c="dimmed" py="xl">
                      İzin kaydı bulunamadı.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>

    {/* YENİ İZİN TALEBİ */}
    <Modal
      opened={opened}
      onClose={close}
      title="Yeni İzin Talebi"
      size="lg"
      centered
    >
      <Stack>
        <Select
          required
          searchable
          label="Personel"
          data={employees.map((x) => ({
            value: String(x.id),
            label: `${x.first_name} ${x.last_name}`,
          }))}
          value={form.employeeId}
          onChange={(value) =>
            setForm((x) => ({
              ...x,
              employeeId: value,
            }))
          }
        />

        <Select
          required
          label="İzin Tipi"
          data={leaveTypes.map((x) => ({
            value: String(x.id),
            label: x.name,
          }))}
          value={form.typeId}
          onChange={(value) =>
            setForm((x) => ({
              ...x,
              typeId: value,
            }))
          }
        />

        <SimpleGrid cols={2}>
          <TextInput
            required
            label="Başlangıç"
            type="date"
            value={form.start}
            onChange={(e) => {
              const value = e.currentTarget.value;

              setForm((x) => ({
                ...x,
                start: value,
              }));
            }}
          />

          <TextInput
            required
            label="Bitiş"
            type="date"
            min={form.start || undefined}
            value={form.end}
            onChange={(e) => {
              const value = e.currentTarget.value;

              setForm((x) => ({
                ...x,
                end: value,
              }));
            }}
          />
        </SimpleGrid>

        <SimpleGrid cols={2}>
          <NumberInput
            label="İzin Süresi"
            value={calculatedDays}
            readOnly
            suffix=" gün"
          />

          <TextInput
            label="İşe Dönüş"
            type="date"
            value={returnDate}
            readOnly
          />
        </SimpleGrid>

        <Textarea
          label="Açıklama / Gerekçe"
          value={form.reason}
          onChange={(e) => {
            const value = e.currentTarget.value;

            setForm((x) => ({
              ...x,
              reason: value,
            }));
          }}
        />

        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={close}
          >
            Vazgeç
          </Button>

          <Button
            disabled={
              !form.employeeId ||
              !form.typeId ||
              !calculatedDays
            }
            onClick={createLeave}
          >
            Talebi Oluştur
          </Button>
        </Group>
      </Stack>
    </Modal>

    {/* İZİN REDDETME */}
    <Modal
      opened={Boolean(rejectingLeave)}
      onClose={() => setRejectingLeave(null)}
      title="İzin Talebini Reddet"
      centered
    >
      <Stack>
        <Textarea
          required
          label="Red Nedeni"
          value={rejectionReason}
          onChange={(e) =>
            setRejectionReason(e.currentTarget.value)
          }
        />

        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => setRejectingLeave(null)}
          >
            Vazgeç
          </Button>

          <Button
            color="red"
            disabled={!rejectionReason.trim()}
            onClick={reject}
          >
            Reddet
          </Button>
        </Group>
      </Stack>
    </Modal>
  </Box>
);
}
